/**
 * Top-bar investigation chip with an expandable popover.
 *
 * The chip is the persistent indicator of "what's the current viewpoint?".
 * Clicking the chevron opens a popover that consolidates all per-mode
 * controls (rename, save, fork, leave, …) in one place — replacing the
 * previous floating panel that used to overlay the lens canvases.
 *
 * Modes wired:
 *   - empty       → silent (chip not rendered)
 *   - exploration → unsaved transient view; popover lets the user name it
 *                   and persist as a saved investigation
 *   - saved       → active saved investigation; popover offers rename,
 *                   leave, fork, open detail
 *   - modified    → active saved investigation with unsaved edits; adds
 *                   "Save changes / Save as new / Discard" to the saved set
 */

import {
  Check,
  ChevronDown,
  GitFork,
  Merge,
  Pencil,
  RefreshCw,
  RotateCcw,
  Save,
  Star,
  Trash2,
  X,
} from 'lucide-react'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useActiveNav } from '@/components/layout/useActiveNav'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useMergedSinceSaved } from '@/features/investigations/hooks/useMergedSinceSaved'
import {
  useViewpointSnapshot,
  type ViewpointSnapshot,
} from '@/features/investigations/hooks/useViewpointSnapshot'
// The popover mutates the per-case investigations store (save / rename / fork
// / discard / …) while rendering in the top bar, i.e. OUTSIDE
// `<InvestigationsStoreProvider>`, so it reaches the module-level store
// directly. Reads already moved to `useViewpointSnapshot`; extracting the
// mutations into a feature hook is a follow-up.
import { getOrCreateInvestigationsStore } from '@/stores/investigations-store' // frontend-rules-ok
import { cn } from '@/utils/cn'
import { errorMessage } from '@/utils/errors'
import { MergedSinceSavedHint } from './_internal/MergedSinceSavedHint'
import type { InvestigationMode } from './_internal/types'

// ─── Connected entry point ───────────────────────────────────────────────────

export function InvestigationBreadcrumbChipConnected() {
  const { caseId } = useActiveNav()
  if (!caseId) return null
  return <ChipWithPopover caseId={caseId} />
}

// ─── Shared style maps ───────────────────────────────────────────────────────

const ACCENT_DOT: Record<InvestigationMode, string> = {
  empty: 'bg-muted-foreground/40',
  exploration: 'bg-warning',
  saved: 'bg-success',
  modified: 'bg-warning',
}

const ACCENT_TEXT: Record<InvestigationMode, string> = {
  empty: 'text-muted-foreground',
  exploration: 'text-warning',
  saved: 'text-success',
  modified: 'text-warning',
}

const CHIP_SURFACE: Record<InvestigationMode, string> = {
  empty: 'border-border bg-muted/40 text-muted-foreground',
  exploration: 'border-warning/40 bg-warning/10',
  saved: 'border-success/45 bg-success/10',
  modified: 'border-warning/50 bg-warning/15',
}

// ─── Snapshot derivation (stable across renders via useShallow) ──────────────

type ChipSnapshot = ViewpointSnapshot

// ─── Chip + Popover ──────────────────────────────────────────────────────────

function ChipWithPopover({ caseId }: { caseId: string }) {
  const snapshot = useViewpointSnapshot(caseId)
  const [open, setOpen] = useState(false)
  // The merges waiting for a Sync, asked only while a saved investigation is
  // shown as saved (ADR-0079).
  const mergedSinceSaved = useMergedSinceSaved(
    caseId,
    snapshot.mode === 'saved' && snapshot.activeId && snapshot.updatedAt
      ? { id: snapshot.activeId, updatedAt: snapshot.updatedAt }
      : null
  )

  // Idle has no expandable content — drop the chip rather than render a
  // ghost trigger that does nothing.
  if (snapshot.mode === 'empty') return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <ChipTrigger snapshot={snapshot} open={open} mergedSinceSaved={mergedSinceSaved} />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        collisionPadding={8}
        className="w-[320px] gap-0 overflow-hidden p-0"
      >
        <PopoverBody
          caseId={caseId}
          snapshot={snapshot}
          mergedSinceSaved={mergedSinceSaved}
          onClose={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  )
}

// ─── Chip trigger ────────────────────────────────────────────────────────────

interface ChipTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  snapshot: ChipSnapshot
  open: boolean
  /** Entities of the saved copy merged away since the save, waiting for a Sync. */
  mergedSinceSaved: number
}

const ChipTrigger = forwardRef<HTMLButtonElement, ChipTriggerProps>(function ChipTrigger(
  { snapshot, open, mergedSinceSaved, ...rest },
  ref
) {
  const { t } = useTranslation()
  const dotPulses = snapshot.mode === 'exploration' || snapshot.mode === 'modified'
  return (
    <button
      ref={ref}
      type="button"
      data-mode={snapshot.mode}
      data-state={open ? 'open' : 'closed'}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-2.5 py-2 md:py-1',
        'text-3xs font-semibold uppercase tracking-[0.16em] transition-colors',
        'cursor-pointer hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        CHIP_SURFACE[snapshot.mode]
      )}
      title={snapshot.investigationName}
      {...rest}
    >
      <span className="relative flex size-1.5 items-center justify-center">
        {dotPulses && (
          <span
            className={cn(
              'absolute inline-flex size-full animate-ping rounded-full opacity-70',
              ACCENT_DOT[snapshot.mode]
            )}
          />
        )}
        <span
          className={cn('relative inline-flex size-1.5 rounded-full', ACCENT_DOT[snapshot.mode])}
        />
      </span>
      <span className={cn(ACCENT_TEXT[snapshot.mode], 'max-md:sr-only')}>
        {t(`investigations.graph.chip.status.${snapshot.mode}`)}
      </span>
      {snapshot.investigationName && (
        <>
          <span className="text-muted-foreground/50 max-md:hidden">·</span>
          {/* Phone: 10ch fits beside the view crumb at 375px; wider phones
              hand their extra width to the name, up to the desktop cap. */}
          <span className="max-w-[clamp(10ch,calc(100vw-19.5rem),24ch)] truncate normal-case tracking-normal text-foreground/85 md:max-w-[24ch]">
            {snapshot.investigationName}
          </span>
        </>
      )}
      {snapshot.entityCount + snapshot.snippetCount > 0 && (
        <span className="font-mono tabular-nums opacity-70">
          {snapshot.entityCount + snapshot.snippetCount}
        </span>
      )}
      {mergedSinceSaved > 0 && (
        <Merge
          role="img"
          aria-label={t('investigations.graph.chip.mergedSinceSavedMark')}
          className="size-3 text-warning"
        />
      )}
      <ChevronDown
        className={cn(
          'size-3 text-muted-foreground transition-transform duration-200',
          open && 'rotate-180'
        )}
      />
    </button>
  )
})

// ─── Popover body ────────────────────────────────────────────────────────────

function PopoverBody({
  caseId,
  snapshot,
  mergedSinceSaved,
  onClose,
}: {
  caseId: string
  snapshot: ChipSnapshot
  /** Entities of the saved copy merged away since the save, waiting for a Sync. */
  mergedSinceSaved: number
  onClose: () => void
}) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation()
  const storeApi = getOrCreateInvestigationsStore({ caseId })
  const [editing, setEditing] = useState(false)

  // The popover always treats the title as editable: in 'exploration' mode
  // the rename writes back to `transient.suggestedTitle`; in 'saved' /
  // 'modified' it patches the active investigation's title.
  const handleRename = async (next: string) => {
    const trimmed = next.trim()
    if (!trimmed) return
    const state = storeApi.getState()
    if (state.transient) {
      const transient = state.transient
      state.setTransient(transient.graphView, trimmed, transient.fromInsightId)
      toast.success('Renamed')
      return
    }
    if (state.activeId) {
      try {
        await state.updateActive({ title: trimmed })
        toast.success('Renamed')
      } catch (err) {
        toast.error(`Rename failed: ${(err as Error).message}`)
        throw err
      }
    }
  }

  const handleSaveAsInvestigation = async () => {
    try {
      await storeApi.getState().saveTransient()
      toast.success('Investigation saved')
      onClose()
    } catch (err) {
      toast.error(`Save failed: ${(err as Error).message}`)
    }
  }

  const handleSaveChanges = async () => {
    try {
      await storeApi.getState().saveActive()
      toast.success('Changes saved')
      onClose()
    } catch (err) {
      toast.error(`Save failed: ${(err as Error).message}`)
    }
  }

  const handleSaveAsNew = async () => {
    try {
      const state = storeApi.getState()
      if (!state.activeId) return
      const inv = state.saved.find(i => i.id === state.activeId)
      if (!inv) return
      state.setTransient(inv.graphView, `Copy of ${inv.title}`)
      await state.saveTransient()
      toast.success('Saved as new investigation')
      onClose()
    } catch (err) {
      toast.error(`Save As failed: ${(err as Error).message}`)
    }
  }

  const handleDiscard = () => {
    storeApi.getState().discardChanges()
    toast.message('Changes discarded')
  }

  const handleLeave = () => {
    const state = storeApi.getState()
    if (state.transient) state.clearTransient()
    if (state.activeId) state.setActive(null)
    // Drop the `?investigation=…` deep-link param so page-level effects
    // don't immediately re-activate the investigation we just left.
    if (searchParams.has('investigation')) {
      const next = new URLSearchParams(searchParams)
      next.delete('investigation')
      setSearchParams(next, { replace: true })
    }
    onClose()
  }

  const handleFork = () => {
    const state = storeApi.getState()
    if (!state.activeId) return
    const inv = state.saved.find(i => i.id === state.activeId)
    if (!inv) return
    state.setTransient(inv.graphView, `Fork of ${inv.title}`)
    toast.message('Fork created — save when ready')
    onClose()
  }

  const handleOpenDetail = () => {
    if (!snapshot.activeId) return
    navigate(`/cases/${caseId}/investigations/${snapshot.activeId}`)
    onClose()
  }

  const handleToggleReport = async () => {
    if (!snapshot.activeId) return
    const next = !snapshot.includedInReport
    try {
      await storeApi.getState().updateById(snapshot.activeId, { includedInReport: next })
      toast.success(
        next
          ? t('investigations.page.featured.addedToReport')
          : t('investigations.page.featured.removedFromReport')
      )
    } catch (err) {
      toast.error(
        t('investigations.page.featured.toggleFailed', { message: (err as Error).message })
      )
    }
  }

  const handleSync = async () => {
    const state = storeApi.getState()
    if (!state.activeId) return
    try {
      const result = await state.syncProperties(state.activeId)
      const parts = [t('investigations.graph.chip.syncDone', { count: result.synced })]
      if (result.merged > 0) {
        parts.push(t('investigations.graph.chip.syncMerged', { count: result.merged }))
      }
      if (result.missing > 0) {
        parts.push(t('investigations.graph.chip.syncMissing', { count: result.missing }))
      }
      const notify = result.missing > 0 ? toast.warning : toast.success
      notify(parts.join(' '))
    } catch (err) {
      toast.error(
        t('investigations.graph.chip.syncFailed', { message: errorMessage(err, String(err)) })
      )
    }
  }

  return (
    <div data-slot="investigation-chip-popover" data-mode={snapshot.mode}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <span className="relative flex size-2 items-center justify-center">
            {(snapshot.mode === 'exploration' || snapshot.mode === 'modified') && (
              <span
                className={cn(
                  'absolute inline-flex size-full animate-ping rounded-full opacity-70',
                  ACCENT_DOT[snapshot.mode]
                )}
              />
            )}
            <span
              className={cn('relative inline-flex size-2 rounded-full', ACCENT_DOT[snapshot.mode])}
            />
          </span>
          <span className="text-3xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {t('investigations.graph.chip.investigation')}
          </span>
          <span className="text-muted-foreground/40">·</span>
          <span
            className={cn(
              'text-3xs font-semibold uppercase tracking-[0.18em]',
              ACCENT_TEXT[snapshot.mode]
            )}
          >
            {t(`investigations.graph.chip.status.${snapshot.mode}`)}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="-mr-1 flex size-8 items-center justify-center rounded-full text-muted-foreground/60 hover:bg-foreground/5 hover:text-foreground md:size-5"
          aria-label={t('investigations.graph.chip.close')}
        >
          <X className="size-3" />
        </button>
      </div>

      {/* Title */}
      <div className="px-4 pb-3">
        {editing ? (
          <TitleEditor
            initial={
              snapshot.investigationName ??
              t(
                snapshot.isTransient
                  ? 'investigations.graph.chip.untitledExploration'
                  : 'investigations.graph.chip.untitled'
              )
            }
            onCommit={async next => {
              await handleRename(next)
              setEditing(false)
            }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="-mx-1 block w-full rounded px-1 text-left text-sm font-semibold leading-tight tracking-tight text-foreground transition-colors hover:bg-foreground/5"
            title={t('investigations.graph.chip.clickToRename')}
          >
            {snapshot.investigationName ??
              t(
                snapshot.isTransient
                  ? 'investigations.graph.chip.untitledExploration'
                  : 'investigations.graph.chip.untitled'
              )}
          </button>
        )}
        {snapshot.isTransient && !editing && (
          <p className="mt-0.5 text-3xs text-muted-foreground">
            {t('investigations.graph.chip.transientHint')}
          </p>
        )}
      </div>

      {/* Scope */}
      <div className="border-t border-border/50 bg-muted/20 px-3 py-3">
        <div className="mb-2 px-1 text-4xs font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">
          {t('investigations.graph.chip.scope')}
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <ScopeCell
            label={t('investigations.graph.chip.entities')}
            value={snapshot.entityCount}
            accent
          />
          <ScopeCell label={t('investigations.graph.chip.edges')} value={snapshot.edgeCount} />
          <ScopeCell label={t('investigations.graph.chip.notes')} value={snapshot.noteCount} />
          <ScopeCell label="Source snippets" value={snapshot.snippetCount} />
        </div>
      </div>

      <MergedSinceSavedHint count={mergedSinceSaved} />

      {/* Actions */}
      <div className="flex flex-wrap gap-1.5 border-t border-border/50 bg-muted/10 px-3 py-2 max-md:[&_[data-slot=button]]:h-9">
        {snapshot.mode === 'exploration' && (
          <>
            <Button variant="solid" size="xs" onClick={handleSaveAsInvestigation}>
              <Save />
              {t('investigations.graph.chip.saveAsInvestigation')}
            </Button>
            <Button variant="ghost" size="xs" onClick={handleLeave}>
              <RotateCcw />
              {t('investigations.graph.chip.reset')}
            </Button>
          </>
        )}
        {snapshot.mode === 'saved' && (
          <>
            <Button variant="outline" size="xs" onClick={handleOpenDetail}>
              <Pencil />
              {t('investigations.graph.chip.openDetail')}
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={handleToggleReport}
              disabled={snapshot.readOnly}
              title={
                snapshot.includedInReport
                  ? t('investigations.page.featured.removeFromReport')
                  : t('investigations.page.featured.addToReport')
              }
            >
              <Star className={cn(snapshot.includedInReport && 'fill-current text-primary')} />
              {snapshot.includedInReport
                ? t('investigations.page.featured.inReport')
                : t('investigations.page.featured.addToReport')}
            </Button>
            <Button variant="ghost" size="xs" onClick={handleFork}>
              <GitFork />
              {t('investigations.graph.chip.fork')}
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={handleSync}
              title={t('investigations.graph.chip.syncTitle')}
            >
              <RefreshCw />
              {t('investigations.graph.chip.sync')}
            </Button>
            <Button variant="ghost" size="xs" onClick={handleLeave}>
              <RotateCcw />
              {t('investigations.graph.chip.leave')}
            </Button>
          </>
        )}
        {snapshot.mode === 'modified' && (
          <>
            <Button variant="solid" size="xs" onClick={handleSaveChanges}>
              <Save />
              {t('investigations.graph.chip.saveChanges')}
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={handleToggleReport}
              disabled={snapshot.readOnly}
              title={
                snapshot.includedInReport
                  ? t('investigations.page.featured.removeFromReport')
                  : t('investigations.page.featured.addToReport')
              }
            >
              <Star className={cn(snapshot.includedInReport && 'fill-current text-primary')} />
              {snapshot.includedInReport
                ? t('investigations.page.featured.inReport')
                : t('investigations.page.featured.addToReport')}
            </Button>
            <Button variant="outline" size="xs" onClick={handleSaveAsNew}>
              <GitFork />
              {t('investigations.graph.chip.saveAsNew')}
            </Button>
            <Button variant="ghost" size="xs" onClick={handleDiscard}>
              <Trash2 />
              {t('investigations.graph.chip.discard')}
            </Button>
            <Button variant="ghost" size="xs" onClick={handleLeave}>
              <RotateCcw />
              {t('investigations.graph.chip.leave')}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function ScopeCell({
  label,
  value,
  accent,
}: {
  label: string
  value: number | string
  accent?: boolean
}) {
  return (
    <Card
      size="compact"
      tone={accent ? 'primary' : 'neutral'}
      className={cn(
        'flex flex-col gap-0.5 rounded-lg border-border/40 px-2.5 py-2',
        accent && 'border-primary/30 bg-primary/5'
      )}
    >
      <span className="text-4xs font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">
        {label}
      </span>
      <span
        className={cn(
          'font-mono text-sm leading-tight tabular-nums',
          accent ? 'text-primary' : 'text-foreground'
        )}
      >
        {typeof value === 'number' ? value.toLocaleString() : value}
      </span>
    </Card>
  )
}

function TitleEditor({
  initial,
  onCommit,
  onCancel,
}: {
  initial: string
  onCommit: (next: string) => void | Promise<void>
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(initial)
  const [busy, setBusy] = useState(false)
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    ref.current?.focus()
    ref.current?.select()
  }, [])

  const submit = async () => {
    const next = draft.trim()
    if (!next || next === initial) {
      onCancel()
      return
    }
    setBusy(true)
    try {
      await onCommit(next)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex items-center gap-1">
      <input
        ref={ref}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault()
            submit()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            onCancel()
          }
        }}
        disabled={busy}
        className={cn(
          '-mx-1 min-w-0 flex-1 rounded border border-border bg-background px-1 py-0.5',
          // 16px text on a phone: anything smaller makes iOS zoom the page on focus.
          'text-base font-semibold leading-tight tracking-tight text-foreground md:text-sm',
          'outline-none focus:border-primary/50',
          busy && 'opacity-60'
        )}
      />
      <button
        type="button"
        onClick={submit}
        disabled={busy}
        title={t('investigations.graph.panel.saveName')}
        className="flex size-8 items-center justify-center rounded text-muted-foreground hover:bg-foreground/5 hover:text-foreground md:size-5"
      >
        <Check className="size-3.5" />
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={busy}
        title={t('investigations.graph.panel.cancel')}
        className="flex size-8 items-center justify-center rounded text-muted-foreground hover:bg-foreground/5 hover:text-foreground md:size-5"
      >
        <X className="size-3.5" />
      </button>
    </div>
  )
}
